import React, { useEffect, useRef, useState } from 'react';
import './InterestLedgerPage.css';

const today = new Date().toISOString().slice(0, 10);
const methods = {
  simple: 'Simple interest',
  monthly: 'Monthly compounded',
  daily: 'Daily compounded',
};
const entryLabels = {
  advance: 'Loan advanced',
  payment: 'Repayment',
  rate: 'Rate changed',
};
const storageKey = 'interest-ledger-v1';
const ledgerVersion = 2;
const supportedVersions = [1, 2];
const themeKey = 'interest-ledger-theme';
const initialWorkspaces = [
  { id: 's1', name: 'Ravi — Home renovation', lender: 'Priya', borrower: 'Ravi', start: '2026-01-04', rate: 12, method: 'simple', password: 'demo123', events: [{ date: '2026-01-04', type: 'advance', amount: 120000, note: 'Initial amount' }, { date: '2026-03-15', type: 'payment', amount: 18000, note: 'Bank transfer' }, { date: '2026-05-01', type: 'rate', rate: 10.5, note: 'Revised rate' }, { date: '2026-07-10', type: 'payment', amount: 12500, note: 'Cash payment' }] },
  { id: 's2', name: 'Asha — Education loan', lender: 'Priya', borrower: 'Asha', start: '2026-02-12', rate: 9.5, method: 'monthly', password: 'demo123', events: [{ date: '2026-02-12', type: 'advance', amount: 75000, note: 'Semester fee' }, { date: '2026-06-12', type: 'payment', amount: 10000, note: 'UPI payment' }] },
  { id: 's3', name: 'Nikhil — Vehicle repair', lender: 'Priya', borrower: 'Nikhil', start: '2026-06-18', rate: 11, method: 'daily', password: 'demo123', events: [{ date: '2026-06-18', type: 'advance', amount: 30000, note: 'Repair advance' }] },
];

const moneyFormat = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
const money = (value) => moneyFormat.format(value || 0);
const formatDate = (value) => new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(`${value}T00:00:00`));
const isDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(new Date(`${value}T00:00:00`).valueOf());
const cleanText = (value) => String(value ?? '').trim();
const effectiveAmount = (event) => (event.lenderShare ? Number(event.amount) * 0.5 : Number(event.amount));
const hasMoneyEntry =(workspace) => workspace.events.some((event) => event.type === 'advance' || event.type === 'payment');
const orderedEvents = (workspace) => workspace.events
  .map((event, index) => ({ ...event, _index: index }))
  .sort((left, right) => left.date.localeCompare(right.date) || left._index - right._index);

function loadLedger() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(storageKey));
    if (supportedVersions.includes(saved?.version) && Array.isArray(saved.workspaces) && saved.workspaces.length && saved.workspaces.every((workspace) => workspace && Array.isArray(workspace.events) && methods[workspace.method] && isDate(workspace.start))) return saved;
  } catch {
    // Start with the demonstration data when browser storage is unavailable or invalid.
  }
  return null;
}

function loadTheme() {
  let stored = null;
  try { stored = window.localStorage.getItem(themeKey); } catch { /* storage unavailable */ }
  if (stored === 'light' || stored === 'dark') return { theme: stored, explicit: true };
  let dark = false;
  try { dark = Boolean(window.matchMedia?.('(prefers-color-scheme: dark)').matches); } catch { /* no matchMedia */ }
  return { theme: dark ? 'dark' : 'light', explicit: false };
}

export function calculateBalance(workspace, asOf = today) {
  let principal = 0;
  let interest = 0;
  let rate = Number(workspace.rate);
  let last = new Date(`${workspace.start}T00:00:00`);
  const end = new Date(`${asOf}T00:00:00`);
  const events = orderedEvents(workspace).filter((event) => event.date <= asOf);
  const accrue = (target) => {
    const days = Math.max(0, (target - last) / 86400000);
    const base = principal + interest;
    const factor = workspace.method === 'simple'
      ? 1
      : workspace.method === 'monthly'
        ? Math.pow(1 + rate / 1200, days / (365 / 12))
        : Math.pow(1 + rate / 36500, days);
    interest += workspace.method === 'simple' ? principal * rate * days / 36500 : base * (factor - 1);
    last = target;
  };

  events.forEach((event) => {
    accrue(new Date(`${event.date}T00:00:00`));
    if (event.type === 'advance') principal += effectiveAmount(event);
    if (event.type === 'payment') {
      const amount = Number(event.amount);
      const interestPaid = Math.min(amount, interest);
      interest -= interestPaid;
      principal -= amount - interestPaid;
    }
    if (event.type === 'rate') rate = Number(event.rate);
  });
  accrue(end);
  return { principal, interest, total: principal + interest, rate, events };
}

function firstOverpayment(workspace, events) {
  const accepted = [];
  for (const { _index, ...event } of orderedEvents({ ...workspace, events })) {
    if (event.type === 'payment' && event.amount > calculateBalance({ ...workspace, events: accepted }, event.date).total + 0.00001) return event;
    accepted.push(event);
  }
  return null;
}

function normalizeWorkspace(raw, index) {
  if (!raw || typeof raw !== 'object' || !isDate(raw.start) || !methods[raw.method] || !Number.isFinite(Number(raw.rate)) || Number(raw.rate) < 0) throw new Error('Invalid workspace');
  const name = cleanText(raw.name);
  const lender = cleanText(raw.lender);
  const borrower = cleanText(raw.borrower);
  if (!name || !lender || !borrower || !Array.isArray(raw.events)) throw new Error('Missing workspace details');
  const events = raw.events.map((event) => {
    if (!event || !entryLabels[event.type] || !isDate(event.date) || event.date < raw.start) throw new Error('Invalid event');
    const normalized = { date: event.date, type: event.type, note: cleanText(event.note) };
    if (event.type === 'rate') {
      normalized.rate = Number(event.rate);
      if (!Number.isFinite(normalized.rate) || normalized.rate < 0) throw new Error('Invalid rate');
    } else {
      normalized.amount = Number(event.amount);
      if (!Number.isFinite(normalized.amount) || normalized.amount <= 0) throw new Error('Invalid amount');
    }
    if (event.lenderShare !== undefined) {
      if (typeof event.lenderShare !== 'boolean' || (event.lenderShare && event.type !== 'advance')) throw new Error('Invalid lender share');
      if (event.lenderShare) normalized.lenderShare = true;
    }
    return normalized;
  });
  const item = { id: cleanText(raw.id) || `imported-${index}-${Date.now()}`, name, lender, borrower, start: raw.start, rate: Number(raw.rate), method: raw.method, password: '', events };
  if (firstOverpayment(item, events)) throw new Error('Overpayment');
  return item;
}

function normalizeWorkspaces(rawWorkspaces) {
  const ids = new Set();
  return rawWorkspaces.map((raw, index) => {
    const workspace = normalizeWorkspace(raw, index);
    if (ids.has(workspace.id)) {
      let suffix = 0;
      do { workspace.id = `imported-${index}-${Date.now()}-${suffix++}`; } while (ids.has(workspace.id));
    }
    ids.add(workspace.id);
    return workspace;
  });
}

function Dialog({ eyebrow, title, onClose, children }) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const invoker = document.activeElement;
    ref.current.querySelector('input, select, button')?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeRef.current();
      } else if (event.key === 'Tab') {
        const focusable = [...ref.current.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((element) => element.type !== 'hidden');
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (invoker && document.contains(invoker)) invoker.focus();
    };
  }, []);

  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="dialog" role="dialog" aria-modal="true" aria-labelledby="il-dialog-title" ref={ref}>
      <div className="dialog-header">
        <div><p className="eyebrow">{eyebrow}</p><h2 id="il-dialog-title">{title}</h2></div>
        <button className="btn btn-quiet icon-button" type="button" onClick={onClose} aria-label="Close dialog">×</button>
      </div>
      {children}
    </section>
  </div>;
}

function CancelRow({ onClose, children }) {
  return <div className="form-actions"><button className="btn btn-secondary" type="button" onClick={onClose}>Cancel</button>{children}</div>;
}

function WorkspaceDialog({ workspace, onSave, onClose }) {
  const creating = !workspace;
  const locked = !creating && hasMoneyEntry(workspace);
  const [error, setError] = useState('');

  const submit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    const fail = (message, field) => { setError(message); form.elements[field]?.focus(); };
    if (!cleanText(data.name) || !cleanText(data.lender) || !cleanText(data.borrower)) return fail('Enter a workspace name, lender, and borrower.', 'name');
    if (!isDate(data.start) || data.start > today) return fail('Choose a start date up to today.', 'start');
    if (!Number.isFinite(Number(data.rate)) || Number(data.rate) < 0) return fail('Enter a non-negative annual rate.', 'rate');
    if (!methods[data.method]) return fail('Choose an interest method.', 'method');
    if (creating && String(data.password).length < 4) return fail('Create a deletion password with at least four characters.', 'password');
    onSave({ name: cleanText(data.name), lender: cleanText(data.lender), borrower: cleanText(data.borrower), start: data.start, rate: Number(data.rate), method: data.method, password: data.password });
  };

  return <Dialog eyebrow={creating ? 'New independent record' : 'Workspace setup'} title={creating ? 'Create workspace' : 'Update workspace'} onClose={onClose}>
    <p>{locked ? 'The start date, method, and starting rate are locked after the first money record. Use a dated rate-change entry for a new rate.' : 'Set the people and terms that define this independent ledger.'}</p>
    <form onSubmit={submit}>
      <div className="field-grid">
        <label>Workspace name<input required name="name" defaultValue={workspace?.name || ''} /></label>
        <label>Start date<input required name="start" type="date" max={today} defaultValue={workspace?.start || today} readOnly={locked} /></label>
        <label>Lender<input required name="lender" defaultValue={workspace?.lender || ''} /></label>
        <label>Borrower<input required name="borrower" defaultValue={workspace?.borrower || ''} /></label>
        <label>Annual rate (%)<input required name="rate" min="0" step="0.01" type="number" defaultValue={workspace?.rate ?? ''} readOnly={locked} /></label>
        <label>Interest method
          {locked
            ? <><input readOnly value={methods[workspace.method]} /><input type="hidden" name="method" value={workspace.method} /></>
            : <select name="method" defaultValue={workspace?.method || 'simple'}>{Object.entries(methods).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>}
        </label>
        <label className="field-full">Deletion password <span className="field-help">Required to delete this workspace. Exported backups never include it.</span>
          <input required={creating} name="password" minLength={4} type="password" defaultValue={workspace?.password || ''} />
        </label>
      </div>
      <p className="field-error" role="alert">{error}</p>
      <CancelRow onClose={onClose}><button className="btn" type="submit">{creating ? 'Create workspace' : 'Save setup'}</button></CancelRow>
    </form>
  </Dialog>;
}

function RemoveEntryDialog({ event, onConfirm, onClose }) {
  const [error, setError] = useState('');
  const checkRef = useRef(null);
  const confirm = () => {
    if (!checkRef.current.checked) { setError('Confirm that you want to remove this record.'); checkRef.current.focus(); return; }
    onConfirm();
  };
  return <Dialog eyebrow="Remove record" title="Remove this ledger record?" onClose={onClose}>
    <p>{event.note || entryLabels[event.type]} on {formatDate(event.date)} will be removed and the payable amount recalculated.</p>
    <label className="check"><input type="checkbox" ref={checkRef} /> <span>I understand that this changes the ledger history.</span></label>
    <p className="field-error" role="alert">{error}</p>
    <CancelRow onClose={onClose}><button className="btn btn-danger" type="button" onClick={confirm}>Remove record</button></CancelRow>
  </Dialog>;
}

function DeleteWorkspaceDialog({ workspace, count, onDelete, onClose }) {
  const [error, setError] = useState('');
  const submit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    const fail = (message, field) => { setError(message); form.elements[field]?.focus(); };
    if (count === 1) return fail('Create or import another workspace before deleting the last one.', 'password');
    if (data.password !== workspace.password) return fail('That password does not match this workspace.', 'password');
    if (!data.acknowledge) return fail('Confirm the irreversible action before deleting.', 'acknowledge');
    onDelete();
  };
  return <Dialog eyebrow="Danger zone" title={`Delete ${workspace.name}?`} onClose={onClose}>
    <p>This removes all of its records and settings from this browser. It cannot be restored unless it was exported first.</p>
    <form onSubmit={submit}>
      <label>Workspace password<input required name="password" type="password" autoComplete="current-password" placeholder={workspace.password === 'demo123' ? 'Demo password: demo123' : undefined} /></label>
      <label className="check"><input required name="acknowledge" type="checkbox" /> <span>I understand this action is irreversible.</span></label>
      <p className="field-error" role="alert">{error}</p>
      <CancelRow onClose={onClose}><button className="btn btn-danger" type="submit">Delete workspace</button></CancelRow>
    </form>
  </Dialog>;
}

function ImportConfirmDialog({ pending, onConfirm, onClose }) {
  const [error, setError] = useState('');
  const checkRef = useRef(null);
  const confirm = () => {
    if (!checkRef.current.checked) { setError('Confirm that you want to replace the current data.'); checkRef.current.focus(); return; }
    onConfirm();
  };
  return <Dialog eyebrow="Replace local collection" title="Replace current data?" onClose={onClose}>
    <p>This backup has <strong>{pending.workspaces.length} workspace(s)</strong>. Importing replaces every workspace currently shown here. Imported workspaces will have no deletion password until you set one in Workspace setup.</p>
    <label className="check"><input type="checkbox" ref={checkRef} /> <span>I understand this replaces the current data.</span></label>
    <p className="field-error" role="alert">{error}</p>
    <CancelRow onClose={onClose}><button className="btn btn-danger" type="button" onClick={confirm}>Replace data</button></CancelRow>
  </Dialog>;
}

function ImportErrorDialog({ onClose }) {
  return <Dialog eyebrow="Import failed" title="Could not read that backup" onClose={onClose}>
    <div className="notice notice-danger">Choose a valid Interest Ledger version 1 JSON backup containing at least one complete workspace.</div>
    <div className="form-actions"><button className="btn" type="button" onClick={onClose}>Close</button></div>
  </Dialog>;
}

function Composer({ workspace, editing, onAdd, onUpdate, onCancel }) {
  const record = editing?.event;
  const [type, setType] = useState(record?.type || 'advance');
  const [shared, setShared] = useState(Boolean(record?.lenderShare));
  const [amountText, setAmountText] = useState(record && record.type !== 'rate' ? String(record.amount) : '');
  const [error, setError] = useState('');
  const isRate = type === 'rate';
  const isShared = shared && type === 'advance';
  const entered = Number(amountText);

  const submit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    const entry = { date: data.date, type, note: cleanText(data.note) };
    if (isRate) entry.rate = Number(data.rate);
    else entry.amount = Number(data.amount);
    if (isShared) entry.lenderShare = true;
    let message = '';
    if (!isDate(entry.date) || entry.date < workspace.start || entry.date > today) message = `Choose a date from ${workspace.start} through today.`;
    else if (isRate && (!Number.isFinite(entry.rate) || entry.rate < 0)) message = 'Enter a non-negative annual rate.';
    else if (!isRate && (!Number.isFinite(entry.amount) || entry.amount <= 0)) message = 'Enter a positive amount.';
    else if (!record && entry.type === 'payment' && entry.amount > calculateBalance(workspace, entry.date).total + 0.00001) message = 'This repayment exceeds the amount due on that effective date. Enter a smaller amount.';
    else if (record) {
      const overpaid = firstOverpayment(workspace, workspace.events.map((item, index) => (index === editing.index ? entry : item)));
      if (overpaid) message = `This change would make the repayment on ${formatDate(overpaid.date)} exceed the amount due.`;
    }
    if (message) {
      setError(message);
      (isRate ? form.elements.rate : form.elements.amount)?.focus();
      return;
    }
    if (record) {
      onUpdate(editing.index, entry);
      return;
    }
    onAdd(entry);
    form.reset();
  };

  const reset = () => { setType('advance'); setShared(false); setAmountText(''); setError(''); };
  const amountHelp = () => {
    if (isRate) return 'This rate applies from the selected date onward.';
    if (type === 'payment') return 'Repayments cannot exceed the amount due on their effective date.';
    if (isShared && entered > 0) return `${money(entered)} entered → ${money(entered / 2)} counted, ${money(entered / 2)} paid by lender.`;
    return 'Enter the amount advanced.';
  };

  return <section className="card form-card" aria-labelledby="il-composer-title">
    <p className="eyebrow">{record ? 'Edit ledger entry' : 'Add to ledger'}</p>
    <h2 id="il-composer-title">{record ? 'Edit record' : 'New record'}</h2>
    <p className="form-intro">{record ? 'Change this record. We will recalculate the amount due and recheck repayments.' : 'Record one dated change. We will recalculate the amount due right away.'}</p>
    <form onSubmit={submit} onReset={reset} noValidate>
      <div className="field-grid">
        <label>Record type
          <select name="type" id="il-entry-type" value={type} disabled={Boolean(record)} onChange={(event) => { setType(event.target.value); setShared(false); }}>
            <option value="advance">Loan advanced</option>
            <option value="payment">Repayment received</option>
            <option value="rate">Change interest rate</option>
          </select>
        </label>
        <label>Date<input required id="il-entry-date" name="date" type="date" min={workspace.start} max={today} defaultValue={record?.date || today} autoFocus={Boolean(record)} /></label>
        <label className="field-full" key={isRate ? 'rate' : 'amount'}>{isRate ? 'New annual rate (%)' : 'Amount (₹)'}
          <input required name={isRate ? 'rate' : 'amount'} min={isRate ? '0' : '0.01'} step="0.01" inputMode="decimal" type="number" defaultValue={isRate ? record?.rate : record?.amount} onChange={(event) => setAmountText(event.target.value)} />
          <span className="field-help">{amountHelp()}</span>
        </label>
        {type === 'advance' && <label className="check field-full"><input type="checkbox" checked={shared} onChange={(event) => setShared(event.target.checked)} /> <span>Lender pays 50%</span></label>}
        <label className="field-full">Note <span className="field-help">Optional, but helpful for tracing the record.</span><input name="note" maxLength={140} defaultValue={record?.note} /></label>
      </div>
      <p className="field-error" role="alert">{error}</p>
      <div className="form-actions">{record ? <button className="btn btn-secondary" type="button" onClick={onCancel}>Cancel</button> : <button className="btn btn-secondary" type="reset">Clear</button>}<button className="btn" type="submit">{record ? 'Save changes' : 'Add record'}</button></div>
    </form>
  </section>;
}

function LedgerRows({ view, onEdit, onRemove }) {
  if (!view.events.length) {
    return <tr><td colSpan="5"><div className="empty"><h3>No activity yet</h3><p>Add the first advance, repayment, or rate change to begin this ledger.</p><button className="btn" type="button" onClick={focusComposer}>Add a record</button></div></td></tr>;
  }
  return view.events.map((event) => <tr key={event._index} id={`il-entry-${event._index}`}>
    <td>{formatDate(event.date)}</td>
    <td><span className={`tag ${event.type === 'payment' ? 'tag-payment' : event.type === 'rate' ? 'tag-rate' : ''}`}>{entryLabels[event.type]}</span></td>
    <td>{event.note || <span aria-label="No note">—</span>}</td>
    <td className="number">{event.type === 'rate' ? `${event.rate}% p.a.` : `${event.type === 'payment' ? '−' : '+'} ${money(effectiveAmount(event))}`}{event.lenderShare && <><br /><span className="tag">50% of {money(event.amount)}</span></>}</td>
    <td><div className="row-actions">
      <button className="btn btn-quiet row-remove" id={`il-edit-${event._index}`} type="button" onClick={() => onEdit(event._index)}>Edit<span className="live-region"> {entryLabels[event.type]} on {formatDate(event.date)}</span></button>
      <button className="btn btn-quiet row-remove" type="button" onClick={() => onRemove(event._index)}>Remove<span className="live-region"> {entryLabels[event.type]} on {formatDate(event.date)}</span></button>
    </div></td>
  </tr>);
}

function focusComposer() {
  const target = document.getElementById('il-entry-type');
  target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  target?.focus();
}

function InterestLedgerPage() {
  const [saved] = useState(loadLedger);
  const [workspaces, setWorkspaces] = useState(() => saved?.workspaces || initialWorkspaces);
  const [selectedId, setSelectedId] = useState(() => saved?.selectedId || 's1');
  const [modal, setModal] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [notice, setNotice] = useState('');
  const [themeState, setThemeState] = useState(loadTheme);
  const importRef = useRef(null);
  const workspace = workspaces.find((item) => item.id === selectedId) || workspaces[0];
  const view = { ...calculateBalance(workspace), asOf: today, method: methods[workspace.method] };
  const dark = themeState.theme === 'dark';

  useEffect(() => {
    try { window.localStorage.setItem(storageKey, JSON.stringify({ version: ledgerVersion, selectedId, workspaces })); } catch { /* storage unavailable */ }
  }, [selectedId, workspaces]);

  useEffect(() => {
    if (themeState.explicit) return undefined;
    const query = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!query) return undefined;
    const follow = (event) => setThemeState({ theme: event.matches ? 'dark' : 'light', explicit: false });
    if (query.addEventListener) { query.addEventListener('change', follow); return () => query.removeEventListener('change', follow); }
    query.addListener?.(follow);
    return () => query.removeListener?.(follow);
  }, [themeState.explicit]);

  const toggleTheme = () => {
    const next = dark ? 'light' : 'dark';
    setThemeState({ theme: next, explicit: true });
    setNotice(`${next === 'dark' ? 'Dark' : 'Light'} theme enabled.`);
    try { window.localStorage.setItem(themeKey, next); } catch { /* storage unavailable */ }
  };
  const updateSelected = (updater) => setWorkspaces((current) => current.map((item) => (item.id === workspace.id ? updater(item) : item)));
  const closeModal = () => setModal(null);
  const editEvent = editIndex === null ? undefined : workspace.events[editIndex];
  const focusEdit = (index) => setTimeout(() => document.getElementById(`il-edit-${index}`)?.focus(), 0);

  const addEntry = (entry) => {
    updateSelected((item) => ({ ...item, events: [...item.events, entry] }));
    setNotice(`${entryLabels[entry.type]} added to ${workspace.name}.`);
  };
  const updateEntry = (index, entry) => {
    updateSelected((item) => ({ ...item, events: item.events.map((existing, position) => (position === index ? entry : existing)) }));
    setEditIndex(null);
    setNotice('Record updated and balance recalculated.');
    focusEdit(index);
  };
  const cancelEdit = () => {
    focusEdit(editIndex);
    setEditIndex(null);
  };
  const saveWorkspace = (details) => {
    if (modal.kind === 'create') {
      const item = { id: `w-${Date.now()}`, ...details, events: [] };
      setWorkspaces((current) => [...current, item]);
      setSelectedId(item.id);
      setEditIndex(null);
      setNotice(`${item.name} created. Add its first record when ready.`);
    } else {
      const locked = hasMoneyEntry(workspace);
      updateSelected((item) => ({ ...item, name: details.name, lender: details.lender, borrower: details.borrower, password: details.password || item.password, start: locked ? item.start : details.start, rate: locked ? item.rate : details.rate, method: locked ? item.method : details.method }));
      setNotice(`${workspace.name} setup saved.`);
    }
    closeModal();
  };
  const removeEntry = () => {
    updateSelected((item) => ({ ...item, events: item.events.filter((_, index) => index !== modal.payload) }));
    setEditIndex(null);
    setNotice('Ledger record removed and balance recalculated.');
    closeModal();
  };
  const deleteWorkspace = () => {
    const next = workspaces.filter((item) => item.id !== workspace.id);
    setWorkspaces(next);
    setSelectedId(next[0].id);
    setEditIndex(null);
    setNotice(`${workspace.name} was deleted.`);
    closeModal();
  };
  const exportBackup = () => {
    const backup = { format: 'interest-ledger-workspace-backup', version: ledgerVersion, exportedAt: new Date().toISOString(), selected: selectedId, workspaces: workspaces.map(({ password, ...item }) => item) };
    const url = URL.createObjectURL(new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'interest-ledger-backup.json';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setNotice('Backup downloaded without deletion passwords.');
  };
  const importBackup = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const backup = JSON.parse(reader.result);
        if (backup.format !== 'interest-ledger-workspace-backup' || !supportedVersions.includes(backup.version) || !Array.isArray(backup.workspaces) || !backup.workspaces.length) throw new Error('Unsupported backup');
        setModal({ kind: 'import-confirm', payload: { workspaces: normalizeWorkspaces(backup.workspaces), selected: backup.selected } });
      } catch {
        setModal({ kind: 'import-error' });
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };
  const confirmImport = () => {
    const { workspaces: imported, selected } = modal.payload;
    setWorkspaces(imported);
    setSelectedId(imported.some((item) => item.id === selected) ? selected : imported[0].id);
    setEditIndex(null);
    setNotice('Backup imported. Set deletion passwords in Workspace setup.');
    closeModal();
  };
  const openImport = () => importRef.current.click();

  let dialog = null;
  if (modal?.kind === 'create') dialog = <WorkspaceDialog onSave={saveWorkspace} onClose={closeModal} />;
  else if (modal?.kind === 'settings') dialog = <WorkspaceDialog workspace={workspace} onSave={saveWorkspace} onClose={closeModal} />;
  else if (modal?.kind === 'remove-entry') dialog = <RemoveEntryDialog event={workspace.events[modal.payload]} onConfirm={removeEntry} onClose={closeModal} />;
  else if (modal?.kind === 'delete') dialog = <DeleteWorkspaceDialog workspace={workspace} count={workspaces.length} onDelete={deleteWorkspace} onClose={closeModal} />;
  else if (modal?.kind === 'import-confirm') dialog = <ImportConfirmDialog pending={modal.payload} onConfirm={confirmImport} onClose={closeModal} />;
  else if (modal?.kind === 'import-error') dialog = <ImportErrorDialog onClose={closeModal} />;

  return <div className="interest-ledger-page" data-theme={themeState.theme}>
    <div className="shell">
      <header className="topbar">
        <div className="brand"><span className="brand-mark" aria-hidden="true">₹</span>Interest Ledger</div>
        <p className="page-note">Data is saved in this browser. Download a backup before changing devices or clearing browser data.</p>
        <div className="top-actions">
          <button className="btn btn-secondary theme-switch" type="button" role="switch" aria-checked={dark} aria-label="Dark theme" onClick={toggleTheme}>
            <span className="theme-icon" aria-hidden="true">{dark ? '☾' : '☀'}</span>
            <span className="theme-label" aria-hidden="true">{dark ? 'Dark theme' : 'Light theme'}</span>
          </button>
          <button className="btn btn-quiet" type="button" onClick={exportBackup}>Export backup</button>
          <button className="btn btn-secondary" type="button" onClick={openImport}>Import</button>
        </div>
      </header>

      <main tabIndex={-1}>
        <div className="app-layout">
          <nav className="navigator" aria-label="Workspaces">
            <div className="card">
              <div className="nav-heading"><h2><span className="mobile-nav-label">Your </span>Workspaces</h2><span aria-label={`${workspaces.length} workspaces`} className="eyebrow">{workspaces.length}</span></div>
              <div className="workspace-list">
                {workspaces.map((item) => <button key={item.id} className="workspace-option" type="button" aria-current={item.id === workspace.id ? 'page' : 'false'} onClick={() => { setSelectedId(item.id); setEditIndex(null); setNotice(`Opened ${item.name}.`); }}>
                  <strong>{item.name}</strong><small>{item.borrower} owes {item.lender}</small><span className="nav-balance">{money(calculateBalance(item).total)}</span>
                </button>)}
              </div>
              <div className="nav-footer">
                <button className="btn btn-secondary" type="button" onClick={openImport}>Import backup</button>
                <button className="btn" type="button" onClick={() => setModal({ kind: 'create' })}>New workspace</button>
              </div>
            </div>
          </nav>

          <section className="workspace-main" aria-labelledby="il-workspace-title">
            <header className="context-header"><div className="context-copy">
              <p className="eyebrow">Agreement · {workspace.borrower} owes {workspace.lender}</p>
              <h1 id="il-workspace-title">{workspace.name}</h1>
              <p className="subhead">An independent ledger with its own dated history and terms.</p>
            </div></header>

            <div className="overview-grid">
              <section className="card balance-card" aria-label="Current balance">
                <div><p className="eyebrow">Total payable</p><div className="balance-number">{money(view.total)}</div><p className="subhead">As of {formatDate(view.asOf)}</p></div>
                <p className="method-note"><strong>{view.method}</strong><br />Calculated from dated activity at the current annual rate.</p>
              </section>
              <section className="card next-action-card" aria-labelledby="il-next-title">
                <div><p className="eyebrow">Next step</p><h2 id="il-next-title">Record a dated change</h2><p>Advances, repayments, and rate changes update the payable amount right away.</p></div>
                <button className="btn" type="button" onClick={focusComposer}>Add record</button>
              </section>
            </div>

            <dl className="metrics" aria-label="Balance details">
              <div className="card metric"><dt>Principal</dt><dd>{money(view.principal)}</dd><small>Outstanding advance</small></div>
              <div className="card metric"><dt>Accrued interest</dt><dd>{money(view.interest)}</dd><small>Included in the total</small></div>
              <div className="card metric"><dt>Current rate</dt><dd>{view.rate}%</dd><small>Annual rate in force</small></div>
              <div className="card metric"><dt>Method</dt><dd>{view.method}</dd><small>Calculation convention</small></div>
            </dl>

            <div className="work-area">
              <section className="card section-card" aria-labelledby="il-activity-title">
                <div className="section-heading"><div><p className="eyebrow">Traceability</p><h2 id="il-activity-title">Ledger activity</h2><p>Ordered by effective date; entries on the same date keep their entered order.</p></div></div>
                <div className="table-wrap"><table>
                  <thead><tr><th scope="col">Date</th><th scope="col">Activity</th><th scope="col">Note</th><th scope="col">Amount / rate</th><th scope="col"><span className="live-region">Actions</span></th></tr></thead>
                  <tbody><LedgerRows view={view} onEdit={setEditIndex} onRemove={(index) => setModal({ kind: 'remove-entry', payload: index })} /></tbody>
                </table></div>
              </section>
              <aside className="side-stack">
                <Composer key={editEvent ? editIndex : 'new'} workspace={workspace} editing={editEvent ? { index: editIndex, event: editEvent } : null} onAdd={addEntry} onUpdate={updateEntry} onCancel={cancelEdit} />
                <section className="card settings-card" aria-labelledby="il-settings-title">
                  <p className="eyebrow">Workspace</p><h2 id="il-settings-title">Keep this ledger accurate</h2>
                  <p>{hasMoneyEntry(workspace) ? 'The starting date, rate, and interest method are locked because this ledger has money activity.' : 'Set its terms before recording the first money entry.'}</p>
                  <button className="btn btn-secondary" type="button" onClick={() => setModal({ kind: 'settings' })}>Workspace setup</button>
                </section>
                <section className="card settings-card danger-zone" aria-labelledby="il-delete-title">
                  <p className="eyebrow">Irreversible action</p><h3 id="il-delete-title">Delete this workspace</h3>
                  <p>Requires the workspace password and a clear confirmation.</p>
                  <button className="btn btn-danger" type="button" onClick={() => setModal({ kind: 'delete' })}>Delete workspace</button>
                </section>
              </aside>
            </div>
          </section>
        </div>
      </main>
    </div>
    <input ref={importRef} className="hidden-input" type="file" accept="application/json,.json" onChange={importBackup} />
    {dialog}
    <div className="live-region" aria-live="polite" aria-atomic="true">{notice}</div>
  </div>;
}

export default InterestLedgerPage;
